import { functions } from '@/core/firebase'
import { getAccountContext } from '@/store/api'
import { httpsCallable } from 'firebase/functions'
import { showLoading, hideLoading } from '@/core/ui'

interface AssignModalOptions {
  // Mode A: Assign unassigned branches TO this brand (Used in Brand List)
  targetBrand?: { brandId: string, brandName: string }
  
  // Mode B: Assign THIS branch TO a chosen brand (Used in Branch List)
  targetBranch?: { branchId: string, branchName: string, currentBrandName?: string }
  
  onSuccess: () => void
}

export function showAssignBranchModal(options: AssignModalOptions): void {
  const { userBranches, userBrands } = getAccountContext()
  
  // determine mode
  const isBranchMode = !!options.targetBranch
  
  let itemsToSelect: { id: string, name: string }[] = []
  let title = ''
  let label = ''
  let emptyMessage = ''

  if (isBranchMode) {
    // Mode B: We are assigning a specific branch -> Select a Brand
    itemsToSelect = userBrands.map(b => ({ id: b.brandId, name: b.brandName }))
    title = `Assign Brand to "${options.targetBranch?.branchName}"`
    label = 'Select a Brand'
    emptyMessage = 'You have not created any brands yet. Please create a brand first.'
  } else {
    // Mode A: We are assigning to a specific brand -> Select Unassigned Branches
    const unassigned = userBranches.filter(b => !b.brandId || b.brandId === null || b.brandId === '')
    itemsToSelect = unassigned.map(b => ({ id: b.branchId, name: b.branchName }))
    title = `Assign Branch to "${options.targetBrand?.brandName}"`
    label = 'Select an unassigned branch'
    emptyMessage = `You have no unassigned branches available to assign to <strong>${options.targetBrand?.brandName}</strong>.`
  }

  const backdrop = document.createElement('div')
  backdrop.id = 'assign-branch-modal-backdrop'
  backdrop.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'
  backdrop.style.backdropFilter = 'blur(4px)'

  const modal = document.createElement('div')
  modal.className = 'bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden transform transition-all'

  let modalBody = ''
  
  if (itemsToSelect.length === 0) {
    modalBody = `
      <div class="p-6 text-center">
        <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
          <svg class="h-6 w-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
          </svg>
        </div>
        <h3 class="text-lg leading-6 font-medium text-gray-900 mb-2">No Options Available</h3>
        <p class="text-sm text-gray-500 mb-6">${emptyMessage}</p>
        <button type="button" id="cancel-assign-branch-btn" class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm">
          Close
        </button>
      </div>`
  } else {
    modalBody = `
      <form id="assign-branch-form-modal" class="p-6">
        <div class="flex items-center justify-between mb-5">
          <h3 class="text-xl font-bold text-gray-900">${title}</h3>
          <button type="button" id="close-assign-branch-modal" class="text-gray-400 hover:text-gray-500 focus:outline-none">
            <span class="sr-only">Close</span>
            <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        ${isBranchMode && options.targetBranch?.currentBrandName ? `
          <div class="mb-4 p-3 bg-blue-50 rounded-md border border-blue-100">
            <p class="text-sm text-blue-800">Current Brand: <span class="font-semibold">${options.targetBranch.currentBrandName}</span></p>
          </div>
        ` : ''}

        <div class="mb-5">
          <label for="assign-selection" class="block text-sm font-medium text-gray-700 mb-2">${label}</label>
          <div class="relative">
            <select id="assign-selection" class="block w-full pl-3 pr-10 py-2.5 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md shadow-sm transition-shadow" required>
              <option value="" disabled selected>Choose one...</option>
              ${itemsToSelect.map(item => 
                `<option value="${item.id}">${item.name}</option>`
              ).join('')}
            </select>
          </div>
        </div>

        <div id="assign-branch-error" class="hidden mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p class="text-sm text-red-600 flex items-center">
            <svg class="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>
            <span id="error-msg-text"></span>
          </p>
        </div>

        <div class="mt-6 flex gap-3 justify-end">
          <button type="button" id="cancel-assign-branch-btn" class="px-4 py-2 bg-white border border-gray-300 rounded-md font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors">
            Cancel
          </button>
          <button type="submit" id="assign-branch-submit-btn" class="px-4 py-2 bg-indigo-600 border border-transparent rounded-md font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 shadow-sm transition-colors disabled:opacity-70 disabled:cursor-not-allowed">
            Confirm Assignment
          </button>
        </div>
      </form>`
  }
  
  modal.innerHTML = modalBody
  backdrop.appendChild(modal)
  document.body.appendChild(backdrop)

  const form = modal.querySelector('#assign-branch-form-modal') as HTMLFormElement
  const closeBtn = modal.querySelector('#close-assign-branch-modal')
  const cancelBtn = modal.querySelector('#cancel-assign-branch-btn')
  const submitBtn = modal.querySelector('#assign-branch-submit-btn') as HTMLButtonElement
  const selectEl = modal.querySelector('#assign-selection') as HTMLSelectElement
  const errorDiv = modal.querySelector('#assign-branch-error') as HTMLElement
  const errorText = modal.querySelector('#error-msg-text') as HTMLElement

  const closeModal = () => {
    modal.classList.remove('scale-100', 'opacity-100')
    modal.classList.add('scale-95', 'opacity-0')
    setTimeout(() => backdrop.remove(), 150)
  }

  requestAnimationFrame(() => {
    modal.classList.add('scale-100', 'opacity-100')
  })

  closeBtn?.addEventListener('click', closeModal)
  cancelBtn?.addEventListener('click', closeModal)
  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) closeModal()
  })

  form?.addEventListener('submit', async (e) => {
    e.preventDefault()
    const selectedId = selectEl.value
    if (!selectedId) {
      if (errorText) errorText.textContent = 'Please make a selection.'
      errorDiv.classList.remove('hidden')
      return
    }

    errorDiv.classList.add('hidden')
    submitBtn.disabled = true
    submitBtn.innerHTML = `<svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg> Processing...`

    try {
      const assignBranch = httpsCallable(functions, 'assignBranchToBrand')
      
      const payload = isBranchMode 
        ? { branchId: options.targetBranch!.branchId, brandId: selectedId }
        : { brandId: options.targetBrand!.brandId, branchId: selectedId }

      const result = await assignBranch(payload)
      const data = result.data as { success: boolean; message: string }
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to assign branch.')
      }
      
      options.onSuccess()
      closeModal()

    } catch (error: any) {
      console.error('[AssignBranchModal] Error:', error)
      if (errorText) errorText.textContent = error.message || 'An unknown error occurred.'
      errorDiv.classList.remove('hidden')
      submitBtn.disabled = false
      submitBtn.textContent = 'Confirm Assignment'
    }
  })
}